import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@fluentui/react-components';
import { LockShieldRegular } from '@fluentui/react-icons';
import { saveMail } from '../../../services/api/dataServiceOutlookQuickSave';
import { ISaveInfo, IAccessProperties } from '../model/QuickSaveModel';
import { IDocumentDetails } from '../../../@types/models';
import { getDocumentDetailComp } from '../../../common/components/home/HomeComponents';
import type {
    InsertableArchiveResult,
    EmailDocumentCategoryResult,
    CaseResult,
    RecordTypeResult,
    CarbonCopyContactResult
} from '../../../services/api/dataServiceOutlookQuickSave';
import { PageHeader } from '../components/PageHeader';
import { AccessRightsForm } from './components/AccessRightsForm';
import { PublicTitleSection } from './components/PublicTitleSection';
import { useAccessRightsData } from './hooks/useAccessRightsData';
import { useAccessReviewStyles } from './styles';
import { navigateToImportArchive } from '../utils/importArchiveNavigation';
import { navigateSafely } from '../../../utils/navigation';

interface PreservedState {
    selectedArchive?: InsertableArchiveResult;
    selectedCategory?: EmailDocumentCategoryResult;
    selectedCase?: CaseResult;
    selectedRecordType?: RecordTypeResult;
    selectedCopyTo?: CarbonCopyContactResult[];
    title?: string;
    isRestricted?: boolean;
}



export interface AccessReviewLocationState {
    saveInfo: ISaveInfo;
    preservedState?: PreservedState;
}

const AccessReviewPage: React.FC = () => {
    const { t } = useTranslation();
    const styles = useAccessReviewStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [documentDetails, setDocumentDetails] = useState<IDocumentDetails | null>(null);
    const [publicTitleOption, setPublicTitleOption] = useState<string>('equal');
    const [manualPublicTitle, setManualPublicTitle] = useState<string>('');

    const state = location.state as AccessReviewLocationState;

    const documentArchive = state?.saveInfo?.documentArchive ? Number.parseInt(state.saveInfo.documentArchive) : undefined;

    const {
        accessCodes,
        lawCodes,
        accessGroups,
        selectedAccessCode,
        setSelectedAccessCode,
        selectedParagraph,
        setSelectedParagraph,
        selectedAccessGroup,
        setSelectedAccessGroup,
        isLoadingAccessCodes,
        isLoadingLawCodes,
        isLoadingAccessGroups,
        error: dataError,
    } = useAccessRightsData({
        documentArchive,
        initialAccessProperties: state?.saveInfo?.accessProps
    });

    const handleBack = () => {
        // Navigate back with preserved state
        navigateSafely(navigate, '/quicksave', { state: undefined });
    };

    // 0 = control hidden, 1 = optional, 2 = mandatory; missing value is treated as optional
    const archiveHasAccessCode = state?.saveInfo?.accessProps?.ArchiveHasAccessCode ?? 1;
    const archiveHasAccessGroup = state?.saveInfo?.accessProps?.ArchiveHasAccessGroup ?? 1;
    const isAccessCodeRequired = archiveHasAccessCode === 2;
    const isAccessGroupRequired = archiveHasAccessGroup === 2;
    const isParagraphRequired = accessCodes.find(ac => ac.Recno.toString() === selectedAccessCode)?.IsParagraphRequired ?? false;
    const canSave = !isSubmitting
        && (!isAccessCodeRequired || Boolean(selectedAccessCode))
        && (!isAccessGroupRequired || Boolean(selectedAccessGroup))
        && (!isParagraphRequired || Boolean(selectedParagraph));

    const handleSave = () => {
        if (!state?.saveInfo) {
            setSubmitError(t('Missing save information'));
            return;
        }

        // Validate required fields
        if (isAccessCodeRequired && !selectedAccessCode) {
            setSubmitError(t('Please select an access code'));
            return;
        }

        if (isParagraphRequired && !selectedParagraph) {
            setSubmitError(t('Please select a paragraph'));
            return;
        }

        if (isAccessGroupRequired && !selectedAccessGroup) {
            setSubmitError(t('Please select an access group'));
            return;
        }

        setIsSubmitting(true);
        setSubmitError('');

        const restrictedAccessProperties: IAccessProperties = {
            ...state.saveInfo.accessProps,
            AccessCode: selectedAccessCode ? Number.parseInt(selectedAccessCode) : 0,
            AccessGroup: selectedAccessGroup ? Number.parseInt(selectedAccessGroup) : 0,
            Paragraph: selectedParagraph || null,
        };

        // Determine public title based on radio selection
        let publicTitle: string | undefined;
        if (publicTitleOption === 'manual' && manualPublicTitle.trim()) {
            publicTitle = manualPublicTitle.trim();
        } else if (publicTitleOption === 'equal') {
            publicTitle = state.saveInfo.documentTitle;
        }

        const updatedSaveInfo: ISaveInfo = {
            ...state.saveInfo,
            accessProps: restrictedAccessProperties,
            publicTitle: publicTitle || undefined,
        };

        void saveMail(
            updatedSaveInfo,
            (response: IDocumentDetails | null) => {
                setIsSubmitting(false);

                // Check if this is an import archive response
                if (response?.IsInImportArchive) {
                    // Navigate to import archive page with error details
                    navigateToImportArchive(navigate, response);
                } else {
                    setDocumentDetails(response);
                }
            },
            (error: string) => {
                setIsSubmitting(false);
                setSubmitError(error);
            }
        );
    };

    // If document saved successfully, show document details
    if (documentDetails) {
        return (
            <div className={styles.accessReviewContainer}>
                {getDocumentDetailComp(documentDetails)}
            </div>
        );
    }

    return (
        <div className={styles.accessReviewContainer}>
            <PageHeader
                title={t('Access Rights')}
                description={t('Modify who can see this document')}
                icon={<LockShieldRegular fontSize={20} />}
            />

            <div className={styles.accessReviewContentContainer}>
                <AccessRightsForm
                    accessCodes={accessCodes}
                    selectedAccessCode={selectedAccessCode}
                    onAccessCodeChange={setSelectedAccessCode}
                    isLoadingAccessCodes={isLoadingAccessCodes}
                    lawCodes={lawCodes}
                    selectedParagraph={selectedParagraph}
                    onParagraphChange={setSelectedParagraph}
                    isLoadingLawCodes={isLoadingLawCodes}
                    accessGroups={accessGroups}
                    selectedAccessGroup={selectedAccessGroup}
                    onAccessGroupChange={setSelectedAccessGroup}
                    isLoadingAccessGroups={isLoadingAccessGroups}
                    showAccessCode={archiveHasAccessCode !== 0}
                    isAccessCodeRequired={isAccessCodeRequired}
                    showAccessGroup={archiveHasAccessGroup !== 0}
                    isAccessGroupRequired={isAccessGroupRequired}
                />

                <PublicTitleSection
                    publicTitleOption={publicTitleOption}
                    onPublicTitleOptionChange={setPublicTitleOption}
                    manualPublicTitle={manualPublicTitle}
                    onManualPublicTitleChange={setManualPublicTitle}
                />

                {(submitError || dataError) && (
                    <div className={styles.accessReviewErrorText}>
                        {submitError || dataError}
                    </div>
                )}

                <div className={styles.accessReviewButtonContainer}>
                    <Button
                        appearance="secondary"
                        onClick={handleBack}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        appearance="primary"
                        onClick={handleSave}
                        disabled={!canSave}
                    >
                        {isSubmitting ? t('Saving...') : t('Save Document')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AccessReviewPage;
